import { SummaryCard } from "@/components/artifacts/ArtifactSummaryCard.tsx";
import {
  FORM_ARTIFACT_TITLE,
  FormArtifactLightbox,
  useOpenFormArtifact,
} from "@/components/artifacts/formArtifactSurface.tsx";
import { useCanvasFollowsCarousel } from "@/components/artifacts/useCanvasFollowsCarousel.ts";
import type { StagedDecision } from "@/shared/artifactDecisions.ts";
import { linkTextOnly } from "@/shared/markdownPlainText.ts";
import { Button } from "@/components/ui/button.tsx";
import { OmniDismissButton } from "@/components/artifacts/OmniDismissButton.tsx";
import {
  OmniCardChrome,
  type OmniCardExpand,
  OmniCardExpandButton,
  OmniCardHeader,
  type OmniCardPagination,
  type OmniCardStaging,
} from "@/components/artifacts/OmniCard.tsx";
import type {
  ArtifactSummary,
  ArtifactSummaryField,
  AskUserFormField,
  UserResponseArtifact,
} from "@/types/artifacts.ts";
import { decodeMultiSelectValue, isFormAnswered } from "@/types/artifacts.ts";
import { Check } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef } from "react";

/** Rows listed in the preview before it folds the rest into "+N more". */
const MAX_PREVIEW_ROWS = 4;

/**
 * What the user answered a field, in the words they were shown. A select stores
 * the option's `value` — a handle id, for `selectHandle` — so the label has to be
 * looked back up or the card would show the user an id they never saw.
 */
function answerText(field: AskUserFormField, values: Record<string, string>): string {
  const raw = values[field.id] ?? "";
  const labelFor = (value: string) =>
    ("options" in field ? field.options.find((o) => o.value === value)?.label : undefined) ?? value;
  if (field.type === "multiSelect") return decodeMultiSelectValue(raw).map(labelFor).join(", ");
  return raw ? labelFor(raw) : "";
}

/**
 * The preview a form shows in place of the server-emitted `biscuit` a heavy
 * artifact carries. A `userResponse` is light — no biscuit, no view — so the
 * summary is built here from what the form already says: the questions still to
 * answer or, once the form is in, the answers that went with it.
 *
 * The headline is the form's name, as on the canvas, not the agent's reply. The
 * reply is already in the thread directly above, and it can run to whole drafts
 * that a one-line headline crushes into a single paragraph (BIG-8781).
 */
export function buildFormSummary(artifact: UserResponseArtifact): ArtifactSummary {
  const fields = artifact.form?.fields ?? [];
  const values = artifact.form?.values;

  if (values) {
    // Answered: the questions are settled, so what's worth showing is what the
    // user said. A field left blank isn't an answer — drop it rather than pad the
    // card with empty rows.
    const answers = fields
      .map((field) => ({
        question: linkTextOnly(field.question),
        answer: answerText(field, values),
      }))
      .filter(({ answer }) => answer !== "");
    const shown = answers.slice(0, MAX_PREVIEW_ROWS);
    return {
      title: FORM_ARTIFACT_TITLE,
      subtitle: "Answered",
      icon: "check",
      fields: [
        ...shown.map(
          ({ question, answer }): ArtifactSummaryField => ({
            type: "value",
            label: question,
            value: answer,
          }),
        ),
        ...(answers.length > shown.length
          ? [{ type: "list" as const, items: [], moreCount: answers.length - shown.length }]
          : []),
      ],
    };
  }

  // Rows in the preview card are plain text, so a question that links what it
  // asks about shows the words rather than the markdown around them.
  const questions = fields.map((field) => linkTextOnly(field.question));
  const shown = questions.slice(0, MAX_PREVIEW_ROWS);

  // Answered in prose rather than submitted, so there are no values to preview —
  // show what was asked, but say plainly that it is settled. Falling through to
  // the "N questions" branch would frame an answered question as outstanding.
  if (artifact.form?.supersededAt) {
    return {
      title: FORM_ARTIFACT_TITLE,
      subtitle: "Answered by reply",
      icon: "check",
      fields: [{ type: "list", items: shown, moreCount: questions.length - shown.length }],
    };
  }

  return {
    title: FORM_ARTIFACT_TITLE,
    subtitle: `${questions.length} question${questions.length === 1 ? "" : "s"}`,
    icon: "helpCircle",
    fields: [{ type: "list", items: shown, moreCount: questions.length - shown.length }],
  };
}

/**
 * Omnibox-overlay biscuit for an **answered** `userResponse` form: the form's
 * name as the headline, a preview card of what the user said, and the
 * follow-up row — the same shape {@link ArtifactBiscuit} gives a heavy artifact,
 * so the two overlay states read as one surface (BIG-7271).
 *
 * A review holds a card after it is decided so the pager can still reach it, and
 * this is what a form shows as once its answers are in. Before that the form is
 * still something to fill in, so the omnibox gives it the stepped
 * {@link OmniArtifactFormCard} — one question at a time — and this card takes
 * over when there is nothing left to step through.
 *
 * The preview click opens the form on the canvas, read-only, to read back what
 * went in.
 */
export const OmniArtifactFormBiscuit = ({
  artifact,
  threadId,
  followUp,
  pagination,
  staging,
  onDismiss,
}: {
  artifact: UserResponseArtifact;
  threadId: number;
  /** The omnibox (or, in a queue, the note input), rendered as the last row. */
  followUp?: ReactNode;
  /** Carousel controls, present only when more than one artifact is surfaced. */
  pagination?: OmniCardPagination;
  /** Review-queue mode (≥2 artifacts). A note staged for this card shows as a
   *  pending `decision`; "Skip" ends the review from the last card. */
  staging?: OmniCardStaging & { decision?: StagedDecision };
  /** Escape, or the header X, dismisses the overlay (throws the queue away). */
  onDismiss?: () => void;
}) => {
  // Opens on the canvas — the single place an artifact opens, mirroring the
  // heavy biscuit — and falls back to the lightbox below where there is none.
  const {
    canvas,
    urn: artifactUrn,
    isLightboxOpen: open,
    setLightboxOpen: setOpen,
    openForm,
  } = useOpenFormArtifact(artifact, threadId, "biscuit");
  const summary = useMemo(() => buildFormSummary(artifact), [artifact]);

  useCanvasFollowsCarousel(artifactUrn, canvas);

  // Left/right arrow steps the carousel, as on the heavy biscuit. Latest-value
  // ref so the document listener attaches once rather than re-binding as the
  // inline `pagination` object changes identity each render.
  const paginationRef = useRef(pagination);
  paginationRef.current = pagination;
  const isSubmittingRef = useRef(false);
  isSubmittingRef.current = staging?.isSubmitting ?? false;
  const hasPagination = !!pagination;
  useEffect(() => {
    if (!hasPagination) return;
    const handler = (e: KeyboardEvent) => {
      const p = paginationRef.current;
      if (!p || isSubmittingRef.current) return;
      if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      // Arrows move the caret inside any editable field — never hijack those.
      const target = e.target;
      if (
        target instanceof HTMLTextAreaElement ||
        target instanceof HTMLInputElement ||
        (target instanceof HTMLElement && target.isContentEditable)
      ) {
        return;
      }
      e.preventDefault();
      if (e.key === "ArrowLeft") p.onPrev();
      else p.onNext();
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, [hasPagination]);

  const form = artifact.form;
  if (!form) return null;

  const stagedDecision = staging?.decision;

  /* An answered form can always be opened, so unlike the heavy biscuit there is
     no capability to resolve: the control is simply drawn. The body button
     keeps its own wording. */
  const expand: OmniCardExpand = {
    onExpand: openForm,
    label: `Expand ${FORM_ARTIFACT_TITLE}`,
  };

  return (
    <>
      <OmniCardChrome onDismiss={onDismiss}>
        {pagination && (
          <OmniCardHeader
            pagination={pagination}
            staging={staging}
            expand={expand}
            onDismiss={onDismiss}
            statsigPrefix="artifact-form-biscuit"
          />
        )}

        {stagedDecision?.kind === "note" && (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Check className="size-3.5 shrink-0" />
            Note added — sent when you finish the queue
          </div>
        )}

        {/* Gap and title treatment match `ArtifactBiscuit` exactly, since the two
            are the same card in different states. */}
        <div className="flex flex-col gap-3.5">
          <div className="flex items-start justify-between gap-2 ps-[5px]">
            <div className="text-base font-medium leading-5">{summary.title}</div>
            {/* A queue keeps its controls up in the pager; without one the
                title line is the header, so they sit at the end of it. Expand is
                drawn here as well as in the pager, matching the heavy biscuit:
                the two are one surface in two states, and a control that appears
                only inside a queue reads as something the queue brought rather
                than something the card can do. */}
            {!pagination && (
              <div className="flex shrink-0 items-center gap-1">
                <OmniCardExpandButton
                  expand={expand}
                  disabled={staging?.isSubmitting}
                  statsigName="artifact-form-biscuit-expand"
                />
                {onDismiss && (
                  <OmniDismissButton
                    onDismiss={onDismiss}
                    disabled={staging?.isSubmitting}
                    statsigName="artifact-form-biscuit-dismiss"
                  />
                )}
              </div>
            )}
          </div>

          <Button
            type="button"
            variant="blank"
            onClick={openForm}
            className="w-full h-auto rounded-md border bg-popover border-border hover:bg-muted/30 p-0 flex-col items-stretch justify-start text-left font-normal whitespace-normal"
            aria-label={isFormAnswered(form) ? "Open your answers" : "Open the full form"}
            data-statsig-name="artifact-form-biscuit-open"
          >
            {/* Keyed by artifact: the biscuit instance deliberately survives
                paging, so without this the card paged to inherits the offset the
                previous one was scrolled to and opens part-way down itself. */}
            <SummaryCard key={artifact.id} summary={summary} />
          </Button>
        </div>

        {/* The follow-up draws its own top rule, as the action rows do, so there
            is no separate divider to keep in step with it. `-mb` hands back the
            row's own centring so the card closes on the air it opens with. */}
        {followUp && <div className="-mb-[7px] flex flex-col">{followUp}</div>}
      </OmniCardChrome>

      {/* Fallback surface where there is no canvas (mobile / gate off) — the same
          form the canvas would have presented. */}
      <FormArtifactLightbox
        artifact={artifact}
        threadId={threadId}
        open={open}
        onOpenChange={setOpen}
      />
    </>
  );
};
